import { FolderKanban, Share2 } from "lucide-react";
import type { Agent } from "@/types";

/**
 * Compact badge(s) showing an agent's relationship to projects.
 *
 * - Owned (created for a project) → tinted pill with the project name.
 * - Shared into other projects → neutral "Shared in N" chip.
 * - Standalone (neither) → renders nothing.
 */
export function AgentProjectBadge({
  agent,
  className = "",
}: Readonly<{
  agent: Pick<Agent, "ownedByProject" | "sharedInProjects">;
  className?: string;
}>) {
  const owned = agent.ownedByProject;
  const shared = agent.sharedInProjects ?? [];

  if (!owned && shared.length === 0) return null;

  const sharedTitle = shared.length > 0 ? `Shared into: ${shared.map((p) => p.name).join(", ")}` : undefined;

  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      {owned && (
        <span
          title={`This agent belongs to the ${owned.name} project`}
          className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full shrink-0 bg-brand-secondary-subtle text-brand-secondary border border-brand-secondary/30"
        >
          <FolderKanban className="h-3 w-3" />
          <span className="truncate max-w-[10rem]">{owned.name}</span>
        </span>
      )}
      {shared.length > 0 && (
        <span
          title={sharedTitle}
          className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full shrink-0 bg-surface-tertiary text-content-tertiary border border-border-hover"
        >
          <Share2 className="h-3 w-3" />
          Shared in {shared.length}
        </span>
      )}
    </span>
  );
}

/**
 * Full-width informational notice describing an agent's project relationship,
 * for use at the top of agent detail / edit modals.
 */
export function AgentProjectNotice({
  agent,
  showVisibilityHint = false,
  className = "",
}: Readonly<{
  agent: Pick<Agent, "ownedByProject" | "sharedInProjects">;
  showVisibilityHint?: boolean;
  className?: string;
}>) {
  const owned = agent.ownedByProject;
  const shared = agent.sharedInProjects ?? [];

  if (!owned && shared.length === 0) return null;

  const joinNames = (names: string[]) =>
    names.map((name, i) => (
      <span key={name}>
        {i > 0 && (i === names.length - 1 ? " and " : ", ")}
        <span className="font-medium">{name}</span>
      </span>
    ));

  // Owned agents get a blue "belongs to project" notice; shared-only agents get
  // a neutral "also available in" notice that makes clear they stay global.
  const isOwned = !!owned;
  const containerClass = isOwned
    ? "bg-blue-500/10 border-blue-500/20 text-blue-300"
    : "bg-surface-secondary border-border-primary text-content-secondary";
  const iconClass = isOwned ? "text-blue-400" : "text-content-tertiary";

  return (
    <div className={`px-4 py-3 border rounded-lg flex items-start gap-3 ${containerClass} ${className}`}>
      {isOwned ? (
        <FolderKanban className={`w-4 h-4 mt-0.5 shrink-0 ${iconClass}`} />
      ) : (
        <Share2 className={`w-4 h-4 mt-0.5 shrink-0 ${iconClass}`} />
      )}
      <div className="text-sm">
        {isOwned ? (
          <>
            <span className="font-medium">Project agent</span>
            <span className="opacity-80">
              {" — "}This agent belongs to {joinNames([owned.name])} and only appears in that project's chat.
              {shared.length > 0 && <> It has also been shared into {joinNames(shared.map((p) => p.name))}.</>}
              {showVisibilityHint && " Visibility controls who can see it within the project."}
            </span>
          </>
        ) : (
          <>
            <span className="font-medium">Shared into projects</span>
            <span className="opacity-80">
              {" — "}This agent is available globally and has also been added to {joinNames(shared.map((p) => p.name))}.
              It still appears in the global agent picker.
            </span>
          </>
        )}
      </div>
    </div>
  );
}
